import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Checkbox, Collapse } from "@mui/material";
import { ProjectDocumentsService, SmartViewService } from "../../../../../../api";
import { CustomButton, IconComponent, icon } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import { DocTree } from "../../../../../../common/components/filters5/filters/documents/components";
import { updateTreeBranch, isNonEmptyArray, searchInTree } from "../../../../../../common/utils";
import { BooleanFilter } from "../../../../../../common/components/filters5/filters";
import { FilterWrapper } from "../../../../../../common/components/filters5/wrappers";
import styles from "./DocumentSelector.module.css";
import { useApi } from "../../../../../../common/hooks";

const extractId = (item) => {
	if (typeof item === "number") {
		return item;
	}
	if (typeof item === "object" && item !== null) {
		return item.id;
	}
	return null;
};

const handleFormatRecursiveDirData = (folders) => {
	if (!isNonEmptyArray(folders)) {
		return null;
	}
	let formatedArray = [];
	folders.forEach((folder) => {
		let formatedElement = {
			...folder,
			id: folder.id,
			label: folder.label,
			expanded: true,
			type: "FOLDER",
			documents: folder.documents?.map((document) => ({ ...document, type: "FILE" })),
		};
		if (isNonEmptyArray(folder?.children)) {
			formatedElement = {
				...formatedElement,
				expanded: true,
				documents: [...(folder?.documents || []), ...handleFormatRecursiveDirData(folder.children)],
			};
		}
		formatedArray = [...formatedArray, formatedElement];
	});
	return formatedArray;
};

const handleFormatDirData = (unformatedData) => {
	let documentList = [];
	let folderList = [];
	if (isNonEmptyArray(unformatedData.documentsInRoot)) {
		documentList = unformatedData.documentsInRoot.map((folder) => ({ ...folder, type: "FILE" }));
	}
	if (isNonEmptyArray(unformatedData.folders)) {
		folderList = handleFormatRecursiveDirData(unformatedData.folders);
	}
	return [...folderList, ...documentList];
};

const getAllIds = (nodes) => {
	let ids = [];
	nodes.forEach((node) => {
		if (node.type === "FILE") {
			ids.push(node.id);
		}
		if (node.childrenIds) {
			ids = [...ids, ...node.childrenIds];
		}
		if (node.documents) {
			ids = [...ids, getAllIds(node.documents)];
		}
	});
	return [...new Set(ids.flat())];
};

const getAllDocuments = (nodes) => {
	let documents = [];
	nodes.forEach((node) => {
		if (node.type === "FILE") {
			documents.push({ id: node.id, name: node.label || node.name || `Document ${node.id}` });
		}
		if (node.childrenIds && node.childrenIds.length > 0) {
			node.childrenIds.forEach((childId) => {
				if (!documents.find((d) => d.id === childId)) {
					documents.push({ id: childId, name: `Document ${childId}` });
				}
			});
		}
		if (node.documents) {
			documents = [...documents, ...getAllDocuments(node.documents)];
		}
	});
	return [...new Map(documents.map((d) => [d.id, d])).values()];
};

const findDocumentById = (nodes, id) => {
	const node = searchInTree({
		tree: nodes,
		idToFind: id,
		identifier: "id",
		iterativeBranchName: "documents",
		additionalConditions: (n) => n.type === "FILE",
	});
	if (node) {
		return { id: node.id, name: node.label || node.name || `Document ${node.id}` };
	}
	return null;
};

export default function DocumentSelector({ defaultProjectId = null, onChange, selectedDocuments = [] }) {
	const { call: getDirectory } = useApi(ProjectDocumentsService.getDirectory);
	const { call: getDocumentNames } = useApi(ProjectDocumentsService.getDocumentNames);
	const { call: searchInDirectoryByDocumentName } = useApi(SmartViewService.searchInDirectoryByDocumentName);

	const contextProjectId = useSelector(({ context }) => context?.project?.id || 0);
	const projectId = useMemo(() => defaultProjectId || contextProjectId, [contextProjectId, defaultProjectId]);

	const [docs, setDocs] = useState([]);
	const [parentCounters, setParentCounters] = useState([]);
	const [allPossibleIds, setAllPossibleIds] = useState([]);
	const [loadingDocuments, setLoadingDocuments] = useState(false);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [filterStatus, setFilterStatus] = useState(null);
	const [selectedIds, setSelectedIds] = useState([]);
	const [pendingIds, setPendingIds] = useState([]);
	const [isExpanded, setIsExpanded] = useState(false);

	const searchInputRef = useRef(null);

	const hasPendingChanges = useMemo(() => {
		if (pendingIds.length !== selectedIds.length) {
			return true;
		}
		return !pendingIds.every((id) => selectedIds.includes(id));
	}, [pendingIds, selectedIds]);

	useEffect(() => {
		if (isExpanded && searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [isExpanded]);

	const selectionSummary = useMemo(() => {
		const count = selectedIds.length;
		if (count === 0) {
			return translate("document-center.distribution-notification.documents.placeholder");
		}
		if (count === 1) {
			return translate("document-center.distribution-notification.documents.selected-single");
		}
		return translate("document-center.distribution-notification.documents.selected-plural", { count });
	}, [selectedIds.length]);

	useEffect(() => {
		if (Array.isArray(selectedDocuments)) {
			const newIds = selectedDocuments.map(extractId).filter(Boolean);
			setSelectedIds(newIds);
			setPendingIds(newIds);
		}
	}, [selectedDocuments]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search.length >= 2 ? search : "");
		}, 300);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		if (pendingIds.length === 0) {
			return;
		}
		const visibleIds = new Set(getAllIds(docs));
		const filteredSelection = pendingIds.filter((id) => visibleIds.has(id));
		if (filteredSelection.length !== pendingIds.length) {
			setPendingIds(filteredSelection);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterStatus, docs]);

	const handleUpdateCounterOnGetWithSearch = (folders, result = [], prevFolderId = 0) => {
		if (isNonEmptyArray(pendingIds)) {
			if (!isNonEmptyArray(folders)) {
				return result;
			}
			let temp = result;
			folders.forEach((folder) => {
				temp = [
					...temp,
					{
						parentId: folder.id,
						counter: folder.documents?.filter((doc) => pendingIds.includes(doc.id))?.length || 0,
					},
				];
				temp = handleUpdateCounterOnGetWithSearch(folder.children, temp, folder.id);
				temp = temp.map((t) => {
					if (t.parentId === prevFolderId) {
						return {
							...t,
							counter: t.counter + (temp.find((f) => f.parentId === folder.id)?.counter || 0),
						};
					}
					return t;
				});
			});
			return temp;
		}
		return [];
	};

	const handleUpdateCounterOnGet = (folder) => ({
		parentId: folder.id,
		counter: folder.childrenIds?.filter((childrenId) => pendingIds.includes(childrenId))?.length || 0,
	});

	const handleExtractFolders = (elements) => {
		let newParentCounters = [];
		if (isNonEmptyArray(pendingIds)) {
			newParentCounters = [...parentCounters];
			elements.forEach((element) => {
				const parentCountIndex = newParentCounters.findIndex((p) => p.parentId === element.id);
				if (isNonEmptyArray(element.childrenIds)) {
					if (parentCountIndex >= 0) {
						newParentCounters[parentCountIndex] = handleUpdateCounterOnGet(element);
					} else {
						newParentCounters = [...newParentCounters, handleUpdateCounterOnGet(element)];
					}
				}
			});
		}
		setParentCounters(newParentCounters);
	};

	useEffect(() => {
		setAllPossibleIds(getAllIds(docs));
	}, [docs]);

	useEffect(() => {
		if (docs.length > 0 && selectedIds.length > 0) {
			const needsEnrichment = selectedDocuments.some((d) => typeof d === "number");
			if (needsEnrichment) {
				const docObjects = selectedIds.map(
					(id) => findDocumentById(docs, id) || { id, name: `Document ${id}` }
				);
				onChange(docObjects);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [docs, selectedIds.length]);

	useEffect(() => {
		if (debouncedSearch) {
			const folders = docs.filter((d) => d.type === "FOLDER");
			if (folders.length > 0) {
				setParentCounters(handleUpdateCounterOnGetWithSearch(folders));
			}
		} else {
			handleExtractFolders(docs);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [docs, pendingIds.length]);

	const getNotificationStatusParam = useCallback(() => {
		if (filterStatus === null) {
			return null;
		}
		return filterStatus.value === true ? "NOTIFIED" : "NOT_NOTIFIED";
	}, [filterStatus]);

	const getTree = useCallback(() => {
		const notificationStatus = getNotificationStatusParam();
		getDirectory({ projectId, directoryId: "root", notificationStatus })
			.then((data) => {
				setDocs(data);
			})
			.catch((err) => console.error(err))
			.finally(() => setLoadingDocuments(false));
	}, [getDirectory, projectId, getNotificationStatusParam]);

	const getTreeWithSearch = useCallback(() => {
		searchInDirectoryByDocumentName({ projectId }, { search: debouncedSearch })
			.then((data) => {
				setDocs(handleFormatDirData(data));
			})
			.catch((err) => console.error(err))
			.finally(() => setLoadingDocuments(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch, projectId, searchInDirectoryByDocumentName]);

	useEffect(() => {
		if (!projectId) {
			return;
		}
		setLoadingDocuments(true);
		if (debouncedSearch) {
			getTreeWithSearch();
		} else {
			getTree();
		}
	}, [debouncedSearch, filterStatus, getTree, getTreeWithSearch, projectId]);

	useEffect(() => {
		if (!isNonEmptyArray(pendingIds)) {
			setParentCounters([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingIds.length]);

	const isFolder = (el) => el.type === "FOLDER";

	const setTreeValue = (directory, elements) => {
		setDocs((prev) =>
			updateTreeBranch({
				tree: prev,
				additionalConditions: isFolder,
				idToReplace: directory.id,
				identifier: "id",
				newBranch: {
					...directory,
					documents: elements,
					expanded: !directory.expanded,
				},
				iterativeBranchName: "documents",
			})
		);
	};

	const handleToggleExpand = (directory, onCallBack) => {
		if (!isNonEmptyArray(directory.documents)) {
			const notificationStatus = getNotificationStatusParam();
			getDirectory({ projectId, directoryId: directory.id, notificationStatus })
				.then((data) => {
					setTreeValue(directory, data);
					handleExtractFolders(data);
				})
				.catch((err) => console.error(err))
				.finally(() => {
					if (onCallBack) {
						onCallBack(false);
					}
				});
		} else {
			setDocs((prev) =>
				updateTreeBranch({
					tree: prev,
					additionalConditions: isFolder,
					idToReplace: directory.id,
					identifier: "id",
					newBranch: {
						...directory,
						expanded: directory.filtered !== undefined ? false : !directory.expanded,
						filtered: directory.prevFilteredState,
						prevFilteredState: directory.filtered,
					},
					iterativeBranchName: "documents",
				})
			);
			if (onCallBack) {
				onCallBack(false);
			}
		}
	};

	const handleCalculateNewCounter = (isChecked, affectedelement, ids) => {
		if (isChecked) {
			return affectedelement.counter > ids.length ? affectedelement.counter - ids.length : 0;
		}
		return affectedelement.counter + ids.length;
	};

	const handleAdjustCounters = ({ parentIds, isChecked, selectedIds: ids }) => {
		parentIds.forEach((parentId) => {
			if (parentCounters.find((parentCounter) => parentCounter.parentId === parentId)) {
				setParentCounters((prev) =>
					prev.map((p) => {
						if (p.parentId === parentId) {
							return {
								...p,
								counter: handleCalculateNewCounter(isChecked, p, ids),
							};
						}
						return p;
					})
				);
			} else {
				setParentCounters((prev) => [
					...prev,
					{
						parentId,
						counter: ids.length,
					},
				]);
			}
		});
	};

	const handleCheck = ({ selectedIds: ids, isChecked, parentIds }) => {
		let newElements = [...pendingIds];

		if (isChecked) {
			newElements = newElements.filter((newElement) => !ids.includes(newElement));
		} else {
			newElements = [...new Set([...newElements, ...ids])];
		}

		if (isNonEmptyArray(parentIds)) {
			handleAdjustCounters({ parentIds, isChecked, selectedIds: ids });
		}

		setPendingIds(newElements);
	};

	const filteredPossibleIds = useMemo(() => getAllIds(docs), [docs]);

	const handleSelectAll = (event) => {
		const isChecked = event.target.checked;
		const targetIds = getAllIds(docs);

		if (isChecked) {
			setPendingIds(targetIds);
		} else {
			setPendingIds([]);
		}
	};

	const handleApply = async () => {
		setSelectedIds(pendingIds);

		const localDocs = getAllDocuments(docs);
		const localDocMap = new Map(localDocs.map((d) => [d.id, d]));

		const idsNeedingNames = pendingIds.filter((id) => {
			const doc = localDocMap.get(id);
			return !doc || doc.name.startsWith("Document ");
		});

		if (idsNeedingNames.length > 0) {
			try {
				const fetchedDocs = await getDocumentNames({ projectId, ids: idsNeedingNames });
				fetchedDocs.forEach((doc) => {
					localDocMap.set(doc.id, doc);
				});
			} catch (error) {
				console.error("Failed to fetch document names:", error);
			}
		}

		const docObjects = pendingIds.map((id) => localDocMap.get(id) || { id, name: `Document ${id}` });
		onChange(docObjects);
		setIsExpanded(false);
	};

	return (
		<div className={styles.container}>
			<label className={styles.label} htmlFor="document-selector-toggle">
				{translate("document-center.distribution-notification.documents.title")}
				<span className={styles.required}>*</span>
			</label>

			<div>
				<div
					className={`${styles.dropdownHeader} ${isExpanded ? styles.dropdownHeaderActive : ""}`}
					id="document-selector-toggle"
					role="button"
					tabIndex={0}
					onClick={() => !isExpanded && setIsExpanded(true)}
					onKeyDown={(e) => {
						if (!isExpanded && (e.key === "Enter" || e.key === " ")) {
							setIsExpanded(true);
						}
					}}
				>
					{!isExpanded ? (
						<>
							<span
								className={styles[(selectedIds.length === 0 && "placeholder") || "selectionText"]}
								role="button"
								tabIndex={0}
								onClick={() => setIsExpanded(true)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										setIsExpanded(true);
									}
								}}
							>
								{selectionSummary}
							</span>
							<IconComponent
								className={styles.chevronIcon}
								icon={icon.faChevronDown}
								onClick={() => setIsExpanded(true)}
							/>
						</>
					) : (
						<>
							<input
								ref={searchInputRef}
								className={styles.searchInput}
								placeholder=""
								type="text"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
							<span className={styles.searchIconWrapper}>
								<IconComponent className={styles.searchIcon} icon={icon.faSearch} />
							</span>
							<span
								className={styles.closeChevron}
								role="button"
								tabIndex={0}
								onClick={(e) => {
									e.stopPropagation();
									setIsExpanded(false);
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.stopPropagation();
										setIsExpanded(false);
									}
								}}
							>
								<IconComponent className={styles.chevronIcon} icon={icon.faChevronDown} />
							</span>
						</>
					)}
				</div>

				<Collapse in={isExpanded}>
					<div className={styles.dropdownPanel}>
						<div className={styles.hintText}>
							{translate(
								"document-center.distribution-notification.documents.search-hint",
								"Ecrivez 2 caractères ou plus pour lancer la recherche."
							)}
						</div>

						<div className={styles.filterRow}>
							<div className={styles.filterContainer}>
								<FilterWrapper
									hasClearSelection
									buttonSize="md"
									iconName={icon.faPaperPlane}
									isActive={filterStatus !== null}
									label={translate(
										"document-center.distribution-notification.documents.filter.send-status",
										"Statut d'envoi"
									)}
									value={filterStatus}
									onClearSelection={() => setFilterStatus(null)}
									onRemove={() => setFilterStatus(null)}
								>
									<BooleanFilter
										falseLabel={translate(
											"document-center.distribution-notification.documents.filter.not-notified",
											"Non notifié"
										)}
										filterKey="notificationStatus"
										trueLabel={translate(
											"document-center.distribution-notification.documents.filter.notified",
											"Notifié"
										)}
										value={filterStatus}
										onChange={({ value }) => setFilterStatus({ value })}
									/>
								</FilterWrapper>
							</div>

							<CustomButton
								color="secondary"
								disabled={pendingIds.length === 0}
								size="md"
								variant="outlined"
								onClick={() => {
									setPendingIds([]);
								}}
							>
								{translate("common:btn.clear-select")}
							</CustomButton>
						</div>

						<div className={styles.selectionActions}>
							<Checkbox
								checked={
									isNonEmptyArray(pendingIds) &&
									pendingIds.length ===
										(filterStatus !== null ? filteredPossibleIds.length : allPossibleIds.length)
								}
								indeterminate={
									pendingIds.length > 0 &&
									pendingIds.length <
										(filterStatus !== null ? filteredPossibleIds.length : allPossibleIds.length)
								}
								size="small"
								onChange={handleSelectAll}
							/>
							<span className={styles.selectAllLabel}>
								{translate("document-center.distribution-notification.documents.select-all")}
							</span>
						</div>

						<div className={styles.treeList}>
							{loadingDocuments ? (
								<div className={styles.loading}>{translate("common:results.loading")}</div>
							) : (
								<DocTree
									showNotifiedStatus
									documents={docs}
									hasSearch={!!debouncedSearch}
									mode="folder"
									parentCounters={parentCounters}
									selectedIds={pendingIds}
									onAdjustCounters={handleAdjustCounters}
									onCheck={handleCheck}
									onToggleExpand={handleToggleExpand}
								/>
							)}
						</div>

						<div className={styles.applyFooter}>
							<CustomButton
								fullWidth
								className={pendingIds.length === 0 ? styles.closeButton : ""}
								color="primary"
								disabled={pendingIds.length > 0 && !hasPendingChanges}
								variant="contained"
								onClick={handleApply}
							>
								{pendingIds.length === 0
									? translate("common:btn.close")
									: translate(
											pendingIds.length === 1
												? "document-center.distribution-notification.apply-result"
												: "document-center.distribution-notification.apply-results",
											{ count: pendingIds.length }
									  )}
							</CustomButton>
						</div>
					</div>
				</Collapse>
			</div>
		</div>
	);
}

DocumentSelector.propTypes = {
	defaultProjectId: PropTypes.number,
	onChange: PropTypes.func.isRequired,
	selectedDocuments: PropTypes.arrayOf(
		PropTypes.oneOfType([
			PropTypes.number,
			PropTypes.shape({
				id: PropTypes.number,
				name: PropTypes.string,
			}),
		])
	),
};

DocumentSelector.defaultProps = {
	defaultProjectId: null,
	selectedDocuments: [],
};
